import React, { useState, Dispatch, SetStateAction } from "react";
import { IActionHandler, MessageDisplayType, OrgInfo, Sort, AdminApprovalGroup, ActionHandlerFunction } from "@preveil-api";
import {
  Account,
  AdminErrorMessages,
  DeleteUsersOrgsByEntityIdGroupsAndGroupIdApiResponse,
  GlobalErrorMessages,
  Message,
  MessageAnchors,
  MessageHandlerDisplayType,
  useAppDispatch,
  useDeleteUsersOrgsByEntityIdGroupsAndGroupIdMutation,
  AdminSuccessMessages,
  OrganizationInformation,
  useAppSelector,
  DriveUIActionTypes,
  AdminUIActionTypes
} from "src/common";
import { Loading } from "src/components";
import { CreateModal } from "..";
import { ListView, SidePanel, Toolbar } from "./list-view";
import { adminActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo;
  is_loading: boolean;
  approval_groups: AdminApprovalGroup[];
  setApprovalGroups: Dispatch<SetStateAction<AdminApprovalGroup[]>>;
  organization_info?: OrganizationInformation;
  handleParentAction: ActionHandlerFunction;
  sort: Sort<keyof AdminApprovalGroup>;
  create_modal_show: boolean;
};

function ManageTabComponent(props: AllProps) {
  const { current_account, org_info, approval_groups, setApprovalGroups, organization_info, is_loading, handleParentAction, sort, create_modal_show } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const [copied_approval_group, setCopiedApprovalGroup] = useState<AdminApprovalGroup | undefined>(undefined);
  const [current_selected_group, setCurrentSelectedGroup] = useState<AdminApprovalGroup>();

  const [deleteApprovalGroup] = useDeleteUsersOrgsByEntityIdGroupsAndGroupIdMutation();

  // Description: Delete selected approval groups - handle errors and display messages (displays if an approval group cannot be deleted)
  async function deleteSelectedGroups() {
    const total = selected.length;
    let errors: number = 0;
    let forbidden: number = 0;
    const selected_groups = approval_groups.filter((approval: AdminApprovalGroup) => selected.includes(approval.approval_group_id));
    const delete_responses = await Promise.all(
      selected_groups.map(async (approval: AdminApprovalGroup) => {
        const account_ids = Account.getAccountIdentifiers(current_account);
        const params = {
          account_ids,
          entityId: org_info.org_id,
          groupId: approval.approval_group_id,
        };
        return await deleteApprovalGroup(params)
          .unwrap()
          .then((response: DeleteUsersOrgsByEntityIdGroupsAndGroupIdApiResponse) => {
            if (!!response.errors && response.errors[0].status === "forbidden") {
              forbidden++;
            } else if (!!response.errors) {
              errors++;
            }
            return !!response.errors ? null : approval;
          });
      }),
    );
    const successes = delete_responses.flatMap((response: AdminApprovalGroup | null) => (!!response ? [response] : []));
    const success_ids = successes.map((s: AdminApprovalGroup) => s.approval_group_id);
    if (successes.length > 0) {
      setApprovalGroups(approval_groups.filter((ag: AdminApprovalGroup) => !success_ids.includes(ag.approval_group_id)));
    }
    dispatch(adminActions.setSelected([]));
    if (total === 1) {
      if (successes.length === 1) {
        dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_deleting_approval_group.replace(MessageAnchors.name, successes[0].name))));
      } else {
        const group = selected_groups[0];
        const message = forbidden > 0 ? AdminErrorMessages.error_deleting_approval_group_forbidden : AdminErrorMessages.error_deleting_approval_group;
        dispatch(uiActions.handleRequestErrors(new Message(message.replace(MessageAnchors.name, group.name))));
      }
    } else if (successes.length === 0) {
      const message = forbidden > 0 ? AdminErrorMessages.error_deleting_multiple_approval_groups_forbidden.replace(MessageAnchors.item_count, forbidden.toString()) : AdminErrorMessages.error_deleting_multiple_approval_groups.replace(MessageAnchors.total_count, total.toString()).replace(MessageAnchors.item_count, errors.toString());
      dispatch(uiActions.handleRequestErrors(new Message(message)));
    } else {
      dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_deleting_multiple_approval_groups.replace(MessageAnchors.total_count, successes.length.toString()))));
    }
  }

  /* Description: Handle all children component actions and store it */
  const ManageTabCalls = {
    handleSetSort: (params: "name" | "approvers" | "required_approvers") => {
      handleParentAction({ actionType: DriveUIActionTypes.SetSort, params });
    },
    handleSelect: (params: { approval_group?: AdminApprovalGroup, selected: boolean }) => {
      let new_selected = selected.slice();
      const _id = !!params.approval_group ? params.approval_group.approval_group_id : null;
      if (!!_id) { // Note: individual check
        if (params.selected) {
          !_.find(new_selected, (_selected: string) => (_selected === _id)) &&
            new_selected.push(_id);
        } else {
          const index = _.findIndex(new_selected, (_selected: string) => (_selected === _id));
          index >= 0 && (new_selected.splice(index, 1));
        }
      } else { // Note: all check
        new_selected = params.selected ? approval_groups.map((ag) => ag.approval_group_id) : [];
      }
      dispatch(adminActions.setSelected(new_selected));
    },
    // Description: Sets the current selected group and shows the side panel
    handleShowSideNavMenu: (params?: { approval_group: AdminApprovalGroup}) => {
      const ag = !!params ? params.approval_group : undefined;
      setCurrentSelectedGroup(ag);
    },
    /* Description: refresh the page by calling get logs */
    handleRefresh: () => {
      handleParentAction({ actionType: AdminUIActionTypes.Refresh });
    },
    /* Description: open create modal */
    handleSetShow: () => {
      handleParentAction({ actionType: AdminUIActionTypes.SetShow });
    },
    handleDelete: () => {
      deleteSelectedGroups();
    },
    handlePageErrorMessage: (params: {
      message: string;
      stack?: any;
      display_type?: MessageDisplayType;
    }) => {
      const display_type = !!params.display_type
        ? params.display_type
        : MessageHandlerDisplayType.logger;
      dispatch(
        uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack),
      );
    },
  };

  function handlePageActions(actionObj: IActionHandler) {
    dispatch(uiActions.handleMessageDismiss());
    const callback = `handle${actionObj.actionType}`;
    // Handle local calls:
    if ((ManageTabCalls as any)[callback] instanceof Function) {
      (ManageTabCalls as any)[callback](actionObj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(
        MessageAnchors.actionType,
        actionObj.actionType,
      );
      ManageTabCalls.handlePageErrorMessage({ message, stack: actionObj });
    }
  }

  return <>
    {!is_loading ? <>
      <Toolbar
        handleAction={handlePageActions}
        loading={is_loading}
        approval_groups={approval_groups}
        setCopiedApprovalGroup={setCopiedApprovalGroup}
      ></Toolbar>
      <div id="outer-scope" className="cover-content">
        <ListView
          loading={is_loading}
          approval_groups={approval_groups}
          sort={sort}
          handleAction={handlePageActions}
        ></ListView>
      </div>
      {!!current_selected_group && (
        <SidePanel
          approval_group={current_selected_group}
          handleAction={handlePageActions}
        ></SidePanel>
      )}
      {!!organization_info && <CreateModal
        approval_groups={approval_groups}
        create_modal_show={create_modal_show}
        copied_approval_group={copied_approval_group}
        handleAction={handlePageActions}
        setCopiedApprovalGroup={setCopiedApprovalGroup}
        organization_info={organization_info}
        current_account={current_account}
        org_info={org_info}
      ></CreateModal>}
    </> : <Loading className="in-place" /> }
  </>;
}

export default React.memo(ManageTabComponent);
